import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CrmFieldInput } from "./crm-field-input";
import { FieldShell, NativeSelect } from "./crm-ui";
import { useCrmText } from "@/lib/crm/i18n";
import { fieldDef, LEAD_STAGES, optionLabel, STAGE_FIELDS, type LeadStage } from "@/lib/crm/fields";
import type { CrmMember, CrmRecord } from "@/lib/crm/api";
import type { Draft } from "@/lib/crm/form-values";
import { formatDateTime } from "@/lib/crm/view-engine";
import { Check, ChevronLeft, ChevronRight, Flag, Loader2, X } from "lucide-react";

const CLIP_FIRST = "[clip-path:polygon(0_0,calc(100%_-_12px)_0,100%_50%,calc(100%_-_12px)_100%,0_100%)]";
const CLIP_MID = "[clip-path:polygon(0_0,calc(100%_-_12px)_0,100%_50%,calc(100%_-_12px)_100%,0_100%,12px_50%)]";

interface Props {
  saved: boolean;
  draft: Draft;
  setValue: (key: string, value: any) => void;
  errors: Record<string, string>;
  members: CrmMember[];
  readOnly: boolean;
  busy: boolean;
  /** Fields the API said are missing to leave a stage (STAGE_REQUIREMENTS). */
  missing: { stage: string; fields: string[] } | null;
  onMove: (stage: LeadStage) => void;
  onAccept: () => void;
  /** The client picked in the first stage (its person fields fill the lead). */
  onPickClient?: (record: CrmRecord) => void;
}

/** Lead business process flow: Novo → Qualificar → Atribuir → Aceite de vendas (Dynamics-style chevrons + stage flyout). */
export function CrmBpf({ saved, draft, setValue, errors, members, readOnly, busy, missing, onMove, onAccept, onPickClient }: Props) {
  const { lang, tx } = useCrmText();
  const [openStage, setOpenStage] = useState<LeadStage | null>(null);
  const active = (LEAD_STAGES as readonly string[]).includes(draft.stage) ? (draft.stage as LeadStage) : "new";
  const activeIndex = LEAD_STAGES.indexOf(active);
  const accepted = draft.status === "accepted";
  const closed = draft.isActive === false;
  const shown = openStage;

  // A blocked move opens the stage that still needs fields.
  useEffect(() => {
    if (missing && (LEAD_STAGES as readonly string[]).includes(missing.stage)) setOpenStage(missing.stage as LeadStage);
  }, [missing]);

  const missingError = (key: string) =>
    missing?.fields.includes(key) || (key === "assignedTo" && missing?.fields.includes("assignedTo")) ? tx("err_required") : undefined;

  const assignMode: "user" | "other" = draft.assignedToUserId ? "user" : draft.assignedToName ? "other" : "user";
  const [forceOther, setForceOther] = useState(false);
  const mode = forceOther ? "other" : assignMode;

  const stageField = (key: string) => {
    if (key === "assignedTo") {
      const err = missingError("assignedTo") || errors.assignedToUserId || errors.assignedToName;
      return (
        <FieldShell key={key} label={tx("assignTo")} required error={err}>
          {readOnly ? (
            <div className="flex min-h-9 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm">
              {members.find((m) => m.userId === draft.assignedToUserId)?.name || draft.assignedToName || "—"}
            </div>
          ) : (
            <div className="space-y-2">
              <NativeSelect value={mode} onChange={(e) => {
                const next = e.target.value as "user" | "other";
                setForceOther(next === "other");
                if (next === "other") setValue("assignedToUserId", "");
                else setValue("assignedToName", "");
              }} className="h-8 text-xs" data-testid="bpf-assign-mode">
                <option value="user">{tx("assignUser")}</option>
                <option value="other">{tx("assignOther")}</option>
              </NativeSelect>
              {mode === "user" ? (
                <NativeSelect value={draft.assignedToUserId || ""} onChange={(e) => setValue("assignedToUserId", e.target.value)} data-testid="bpf-assign-user">
                  <option value="">{tx("select")}</option>
                  {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </NativeSelect>
              ) : (
                <Input value={draft.assignedToName || ""} maxLength={200} placeholder={tx("assignOtherPlaceholder")}
                  onChange={(e) => setValue("assignedToName", e.target.value)} data-testid="bpf-assign-name" />
              )}
            </div>
          )}
        </FieldShell>
      );
    }
    const def = fieldDef("lead", key);
    if (!def) return null;
    const required = key === "contactId" || (active === "new" && key === "companyName") || (key === "status" && active === "qualify");
    return (
      <div key={key} className={key === "contactId" ? "sm:col-span-2" : ""}>
        <CrmFieldInput entity="lead" def={def} value={draft[key]} onChange={(v) => setValue(key, v)}
          error={missingError(key) || errors[key]} required={required} readOnly={readOnly || (key === "status" && accepted)} members={members}
          onPickRecord={key === "contactId" ? onPickClient : undefined} lookupPrefill={key === "contactId" ? draft : undefined} />
      </div>
    );
  };

  const statusChip = closed
    ? accepted
      ? tx("processDone")
      : draft.status === "not_qualified" ? tx("processDisqualified") : tx("processClosed")
    : null;

  return (
    <div className="rounded-lg border bg-card" data-testid="bpf">
      <div className="flex items-center gap-2 overflow-x-auto px-2 py-2">
        <span className="hidden shrink-0 pl-1 pr-2 text-xs font-medium text-muted-foreground md:inline">{tx("leadProcess")}</span>
        <ol className="flex min-w-max flex-1 items-center">
          {LEAD_STAGES.map((s, i) => {
            const done = accepted || i < activeIndex;
            const isActive = !closed && i === activeIndex;
            return (
              <li key={s} className={`flex flex-1 items-center ${i > 0 ? "-ml-2.5" : ""}`}>
                <button
                  type="button"
                  onClick={() => setOpenStage((cur) => (cur === s ? null : s))}
                  className={[
                    "relative flex h-9 w-full min-w-[8.5rem] items-center justify-center gap-1.5 px-6 text-xs font-medium transition-colors sm:text-sm",
                    i === 0 ? `${CLIP_FIRST} rounded-l-md` : CLIP_MID,
                    done ? "bg-secondary text-secondary-foreground" : isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                    shown === s ? "font-semibold underline decoration-2 underline-offset-4" : "",
                  ].join(" ")}
                  aria-expanded={shown === s}
                  data-testid={`bpf-stage-${s}`}
                  data-state={done ? "done" : isActive ? "active" : "pending"}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : isActive ? <Flag className="h-3.5 w-3.5" /> : <span className="text-[11px] opacity-70">{i + 1}</span>}
                  <span className="truncate">{optionLabel("stage", s, lang)}</span>
                </button>
              </li>
            );
          })}
        </ol>
        {statusChip && (
          <span className="shrink-0 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground" data-testid="bpf-status">
            {statusChip}{accepted && draft.acceptedAt ? ` · ${formatDateTime(draft.acceptedAt, lang, false)}` : ""}
          </span>
        )}
      </div>

      {shown && (
        <div className="border-t bg-muted/20 p-3" data-testid={`bpf-flyout-${shown}`}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold">
              {optionLabel("stage", shown, lang)}
              {!closed && shown === active && <span className="ml-2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium uppercase text-primary-foreground">{tx("activeFor")}</span>}
            </p>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpenStage(null)} title={tx("close")}><X className="h-4 w-4" /></Button>
          </div>

          {STAGE_FIELDS[shown].length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{STAGE_FIELDS[shown].map(stageField)}</div>
          ) : (
            <p className="max-w-2xl text-sm text-muted-foreground">{tx("acceptExplain")}</p>
          )}

          {missing && missing.stage === shown && (
            <p className="mt-3 text-xs text-destructive" role="alert" data-testid="bpf-missing">
              {tx("stageMissing", {
                fields: missing.fields.map((k) => (k === "assignedTo" ? tx("assignTo") : fieldDef("lead", k)?.[lang] || k)).join(", "),
              })}
            </p>
          )}

          {!readOnly && !closed && saved && shown === active && (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              {activeIndex > 0 && (
                <Button variant="outline" size="sm" onClick={() => onMove(LEAD_STAGES[activeIndex - 1])} disabled={busy} data-testid="bpf-back">
                  <ChevronLeft className="h-4 w-4" /> {tx("stageBack")}
                </Button>
              )}
              {activeIndex < LEAD_STAGES.length - 1 ? (
                <Button size="sm" onClick={() => onMove(LEAD_STAGES[activeIndex + 1])} disabled={busy} data-testid="bpf-next">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} {tx("stageNext")} <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button size="sm" onClick={onAccept} disabled={busy} data-testid="bpf-finish">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {tx("finish")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
